import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { CookieConsentService } from '../../../core/services/cookie-consent.service';
import { SiteService } from '../../../core/services/site.service';

// Published on document root while the banner is visible so any fixed/centered
// overlay (admin modals) can reserve space for it instead of being covered —
// see frontend/src/styles.scss `:root.cookie-banner-open`. The banner's own
// z-index/precedence is untouched; this is spacing only (2026-08-05, modal
// click-hijack fix — "Request Revision" clicks were silently landing on the
// banner because it sat on top of the modal's bottom action row).
const BANNER_HEIGHT_VAR = '--cookie-banner-height';
const BANNER_OPEN_CLASS = 'cookie-banner-open';

/**
 * Cookie banner, two modes (2026-07-04, "cookie consent all sites"):
 *
 * gdpr   (site.ext.enableGdpr): opt-in — analytics fires only after Accept.
 * notice (everyone else):       opt-out — analytics is auto-fired by
 *                               CookieConsentService.initFromStorage; the
 *                               banner informs and offers a working opt-out.
 */
@Component({
  selector: 'app-cookie-consent',
  standalone: false,
  templateUrl: './cookie-consent.component.html',
  styleUrls: ['./cookie-consent.component.scss']
})
export class CookieConsentComponent implements OnInit, OnDestroy {
  visible = false;
  isManaging = false;
  mode: 'gdpr' | 'notice' = 'notice';
  private destroy$ = new Subject<void>();

  private resizeObserver: ResizeObserver | null = null;
  private observedBannerEl: HTMLElement | null = null;
  private windowResizeListener: (() => void) | null = null;

  // The banner's own element is only in the DOM while `visible` is true
  // (*ngIf) — this setter fires with the ElementRef when it appears and with
  // `undefined` when Angular removes it, so start/stop tracks visibility
  // without a separate watcher.
  @ViewChild('bannerEl') set bannerElRef(ref: ElementRef<HTMLElement> | undefined) {
    if (ref?.nativeElement) {
      this.startObservingBanner(ref.nativeElement);
    } else {
      this.stopObservingBanner();
    }
  }

  constructor(
    private cookieConsent: CookieConsentService,
    private siteService: SiteService,
  ) {}

  get hasConsented(): boolean {
    return this.cookieConsent.hasAnalyticsConsent();
  }

  ngOnInit(): void {
    // Baseline: until (or unless) the banner ever renders, the CSS var must
    // read 0px and the root class must be absent — the strict no-op case for
    // every logged-in staff / already-decided visitor.
    this.clearBannerVar();

    // Mode is unknown until the site config loads — don't flash the wrong copy.
    this.siteService.site$.pipe(
      filter(s => s !== null),
      take(1),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.mode = this.siteService.hasFeature('enableGdpr') ? 'gdpr' : 'notice';
      this.visible = !this.cookieConsent.hasDecided();
    });

    this.cookieConsent.openPreferences$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isManaging = true;
      this.visible = true;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopObservingBanner();
  }

  /**
   * Start tracking the live-rendered banner height. The banner restacks to a
   * column under 640px (see .cookie-banner__inner), so its height is not a
   * constant — measure with ResizeObserver rather than hardcoding, and fall
   * back to a window resize listener on the (rare) browser without it. No-op
   * re-entrantly if we're already observing this exact element.
   */
  private startObservingBanner(el: HTMLElement): void {
    if (this.observedBannerEl === el) return;
    this.stopObservingBanner();
    this.observedBannerEl = el;
    this.measureAndPublish();

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.measureAndPublish());
      this.resizeObserver.observe(el);
    } else if (typeof window !== 'undefined') {
      this.windowResizeListener = () => this.measureAndPublish();
      window.addEventListener('resize', this.windowResizeListener);
    }
  }

  private stopObservingBanner(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.windowResizeListener && typeof window !== 'undefined') {
      window.removeEventListener('resize', this.windowResizeListener);
    }
    this.windowResizeListener = null;
    this.observedBannerEl = null;
    this.clearBannerVar();
  }

  private measureAndPublish(): void {
    if (!this.observedBannerEl) return;
    this.publishHeight(this.observedBannerEl.offsetHeight);
  }

  private publishHeight(height: number): void {
    if (typeof document === 'undefined') return; // SSR / no-DOM guard
    const root = document.documentElement;
    root.style.setProperty(BANNER_HEIGHT_VAR, `${Math.max(0, Math.round(height))}px`);
    root.classList.add(BANNER_OPEN_CLASS);
  }

  private clearBannerVar(): void {
    if (typeof document === 'undefined') return; // SSR / no-DOM guard
    const root = document.documentElement;
    root.style.setProperty(BANNER_HEIGHT_VAR, '0px');
    root.classList.remove(BANNER_OPEN_CLASS);
  }

  accept(): void {
    this.cookieConsent.accept();
    this.visible = false;
    this.isManaging = false;
  }

  decline(): void {
    this.cookieConsent.decline();
    this.visible = false;
    this.isManaging = false;
  }

  openPrivacyPolicy(): void {
    this.cookieConsent.openPrivacyPolicy();
  }
}
